import isOfferDisabled from 'utils/isOfferDisabled';
import moment from 'moment';
import validateOfferInputs from './validateOfferInputs';
import {
  DATE_CANNOT_BE_AFTER_90_DAYS,
  DATE_CANNOT_BE_IN_PAST,
  POINT_CHANGE_END_DATE_ERROR_MESSAGE,
} from '../../../content';
import { ENUM_POINT_CHANGE_TYPE } from './checkContents';
import { GOAL_LIMITS } from './goalHelper';
import {
  OFFER_FIELD_MAPPING,
  TIMEZONE_OPTIONS_MAP,
  ValidationErrorMessages,
} from 'constants/index';
import { getEpochTime } from '../../../helper/helper';
import { isValidUrl } from '../../../utils/isValidUrl';
import { KloverPointsActions } from '../offerOptions/kloverPointsConversionEvents';
import { RevenueConversionEvents } from '../offerOptions/revenueConversionEvents';

/**
 * Responsible to check any invalid record in Offer Tune goals
 * @param offerTuneGoals : All offer tune goals
 * @param key : Prop name for data validation
 * @returns : true/false if any invalid record found
 */
const validTuneGoalsPointsProps = (offerTuneGoals: any, key: string) => {
  let res = true;
  if (offerTuneGoals.length > 0) {
    let inValidOfferTuneGoals;
    if (key === 'conversioncapvalue') {
      inValidOfferTuneGoals = offerTuneGoals.find(
        (goal: any) =>
          goal[key] &&
          goal.status !== 'delete' &&
          goal.isconversioncapactive &&
          (isNaN(goal[key]) || (!isNaN(goal[key]) && Number(goal[key] < 0)))
      );
    } else {
      inValidOfferTuneGoals = offerTuneGoals.find(
        (goal: any) =>
          goal[key] &&
          goal.status !== 'delete' &&
          (isNaN(goal[key]) || (!isNaN(goal[key]) && Number(goal[key] < 0)))
      );
    }
    res = inValidOfferTuneGoals ? false : true;
  }
  return res;
};

/**
 * Responsible to check any duplicate goalids are in Offer Tune goals
 * @param offerTuneGoals : All offer tune goals
 * @returns : object with flag of true/false and if true then goalid will be added
 */
const validateDuplicateGoalIDs = (offerTuneGoals: any[]) => {
  const goalIds = offerTuneGoals
    .filter(
      (goal) =>
        goal.goalid !== null && goal.goalid !== '' && goal.status !== 'delete'
    )
    .map((item) => String(item.goalid));

  const seen = new Set();
  let firstDuplicate = null;

  for (const goal of goalIds) {
    if (seen.has(goal)) {
      firstDuplicate = goal;
      break;
    }
    seen.add(goal);
  }

  return firstDuplicate
    ? {
        isDuplicated: true,
        goalid: firstDuplicate,
      }
    : { isDuplicated: false };
};

/**
 * Responsible to check at least single Goal Completion Instructions for Offer Wall Placement if multiple goals are created
 * @param offerTuneGoals : All offer tune goals
 * @returns : object with flag of true/false and if true then goalid will be added
 */
const validateGoalCompletionInstructions = (offerTuneGoals: any[]) => {
  const goals = offerTuneGoals.filter((goal) => goal.status !== 'delete');
  const goalWithEmptyComInstructions = goals.filter(
    (goal) => !goal?.goalcompletioninstructions
  );
  if (goals.length <= 1) return true;
  return !(goals.length > 1 && goalWithEmptyComInstructions.length > 0);
};

/**
 * Responsible to check any blank Goal ID
 * @param offerTuneGoals : All offer tune goals
 * @returns : object with flag of true/false
 */
export const validateGoalIDs = (offerTuneGoals: any[]) => {
  const isBlankGoalId = offerTuneGoals?.find(
    (x) => x.status !== 'delete' && ['', undefined].includes(x.goalid)
  );
  return isBlankGoalId ? true : false;
};

export const validateBlankGoalCompletionOrder = (offerTuneGoals: any[]) => {
  const isBlankGoalId = offerTuneGoals?.find(
    (x) =>
      x.status !== 'delete' && ['', undefined].includes(x.goalcompletionorder)
  );
  return isBlankGoalId ? true : false;
};

export const validTuneGoalsPromoProps = (offerTuneGoals: any[]) => {
  const blankTypes = offerTuneGoals?.find(
    (x) =>
      x.status !== 'delete' && x.ispointschangescheduled && !x.pointschangetype
  );
  if (blankTypes) return false;

  const promoGoals = offerTuneGoals?.filter(
    (x) =>
      x.status !== 'delete' &&
      x.pointschangetype === ENUM_POINT_CHANGE_TYPE.PROMO &&
      x.ispointschangescheduled
  );

  const blankIsPointChangeScheduled = promoGoals?.find(
    (x) =>
      !x.ispointschangescheduled ||
      !x.pointschangestartdate ||
      !x.pointschangeenddate ||
      !x.pointschangestartvalue ||
      !x.pointschangeendvalue
  );
  if (blankIsPointChangeScheduled) return false;
  const isInValidEndDate = promoGoals?.find((x) =>
    moment(x.pointschangeenddate).isSameOrBefore(
      moment(x.pointschangestartdate)
    )
  );

  if (isInValidEndDate) return false;
  return true;
};

export const validTuneGoalsOneTimeProps = (offerTuneGoals: any[]) => {
  const blankTypes = offerTuneGoals?.find(
    (x) =>
      x.status !== 'delete' && x.ispointschangescheduled && !x.pointschangetype
  );
  if (blankTypes) return false;
  const oneTimeGoals = offerTuneGoals?.filter(
    (x) =>
      x.status !== 'delete' &&
      x.pointschangetype === ENUM_POINT_CHANGE_TYPE.ONETIME &&
      x.ispointschangescheduled
  );

  const blankIsPointChangeScheduled = oneTimeGoals?.find(
    (x) =>
      !x.ispointschangescheduled ||
      !x.pointschangestartdate ||
      !x.pointschangestartvalue
  );

  return blankIsPointChangeScheduled ? false : true;
};

const validateDuplicateGoalCompletionOrder = (offerTuneGoals: any[] = []) => {
  const goalCompletionOrder = offerTuneGoals
    .filter(
      (goal) =>
        goal.goalcompletionorder !== null &&
        goal.goalcompletionorder !== '' &&
        goal.status !== 'delete'
    )
    .map((item) => String(item.goalcompletionorder));

  const seen = new Set();
  let firstDuplicate = null;

  for (const order of goalCompletionOrder) {
    if (seen.has(order)) {
      firstDuplicate = order;
      break;
    }
    seen.add(order);
  }

  return firstDuplicate
    ? {
        isDuplicated: true,
        goalcompletionorder: firstDuplicate,
      }
    : { isDuplicated: false };
};

export const validateGoalCompletionOrderRange = (goals) => {
  const unDeletedGoals = goals.filter((item) => item.status !== 'delete');
  const orders = unDeletedGoals.map((goal) =>
    parseInt(goal.goalcompletionorder, 10)
  );

  return orders.every((order) => order >= 0 && order < unDeletedGoals.length); // Each order must be within 0 and total goals count - 1
};

// Add a common validation function for date/time
function validateDateTimeValue(
  dateTime,
  minAllowed,
  maxAllowed,
  startDate,
  timezone
) {
  if (!dateTime) return { isError: false, errorMessage: '' };

  // Convert input date to UTC using the provided timezone
  const dateString = moment(dateTime).format('YYYY-MM-DDTHH:mm');
  const dateTimeInUTC = moment
    .tz(dateString, TIMEZONE_OPTIONS_MAP[timezone])
    .utc();
  const currentUTC = moment().utc();
  const maxAllowedUTC = maxAllowed.utc();

  if (dateTimeInUTC.isBefore(currentUTC)) {
    return {
      isError: true,
      errorMessage: DATE_CANNOT_BE_IN_PAST,
    };
  }
  if (dateTimeInUTC.isAfter(maxAllowedUTC)) {
    return { isError: true, errorMessage: DATE_CANNOT_BE_AFTER_90_DAYS };
  }
  if (startDate) {
    const startDateUTC = moment
      .tz(startDate, TIMEZONE_OPTIONS_MAP[timezone])
      .utc();
    if (dateTimeInUTC.isSameOrBefore(startDateUTC)) {
      return {
        isError: true,
        errorMessage: POINT_CHANGE_END_DATE_ERROR_MESSAGE,
      };
    }
  }
  return { isError: false, errorMessage: '' };
}

export function getDateTimeValidationAndValue(
  date,
  multipleDimensions,
  goal,
  currentOffer
) {
  const minAllowed = moment().startOf('minute');
  const maxAllowed = moment().add(90, 'days').endOf('minute');
  const dateTime = date ? moment(date) : null;

  // Get timezone from goal or current offer
  const timezone =
    goal?.pointschangestimezone || currentOffer?.pointschangestimezone;

  let startDate = null;
  if (multipleDimensions[0] === 'pointschangeenddate') {
    startDate = goal?.pointschangestartdate
      ? moment(goal.pointschangestartdate)
      : currentOffer?.pointschangestartdate
      ? moment(currentOffer.pointschangestartdate)
      : null;
  }

  const { isError, errorMessage } = validateDateTimeValue(
    dateTime,
    minAllowed,
    maxAllowed,
    startDate,
    timezone
  );
  return {
    isError,
    errorMessage,
    formattedValue: dateTime, // or dateTime.toISOString() if you want to store as string
  };
}

/**
 * Responsible to check any blank Goal ID
 * @param offerTuneGoals : All offer tune goals
 * @returns : object with flag of true/false
 */
export const validatePointsAwarded = (offerTuneGoals: any[]) => {
  const isPointsAwarded = offerTuneGoals?.find(
    (x) => x.status !== 'delete' && ['', undefined].includes(x.pointsawarded)
  );
  return isPointsAwarded ? true : false;
};

/**
 * Validates if the number of active goals meets the minimum requirement
 * @param offerTuneGoals : All offer tune goals
 * @returns : true if below minimum, false if meets minimum
 */
export const validateMinGoals = (offerTuneGoals: any[]) => {
  const activeGoals =
    offerTuneGoals?.filter((goal) => goal.status !== 'delete') || [];
  return activeGoals.length < GOAL_LIMITS.MIN_GOALS;
};

const setSaveOfferButton = (currentOffer: any) => {
  const offerTuneGoals =
    currentOffer && currentOffer.offertunegoals
      ? currentOffer.offertunegoals
      : [];
  const isValidTuneGoalsPointsAwarded = validTuneGoalsPointsProps(
    offerTuneGoals,
    'pointsawarded'
  );

  const isValidTuneGoalsId = validTuneGoalsPointsProps(
    offerTuneGoals,
    'goalid'
  );

  const isValidTuneGoalsConversionValue = validTuneGoalsPointsProps(
    offerTuneGoals,
    'conversioncapvalue'
  );

  const isBlankTuneGoalID = validateGoalIDs(offerTuneGoals);

  const isTuneGoalsCompletionOrderNumeric = validTuneGoalsPointsProps(
    offerTuneGoals,
    'goalcompletionorder'
  );

  const isMultipleConversionsEnabled = currentOffer?.multipleconversionsenabled;

  const isTuneGoalsPromoType = isMultipleConversionsEnabled
    ? validTuneGoalsPromoProps(offerTuneGoals)
    : true;
  const isTuneGoalsOneTimeType = isMultipleConversionsEnabled
    ? validTuneGoalsOneTimeProps(offerTuneGoals)
    : true;

  const isBlankGoalCompletionOrder =
    validateBlankGoalCompletionOrder(offerTuneGoals);

  const isDuplicateTuneGoalOrdering =
    validateDuplicateGoalCompletionOrder(offerTuneGoals);

  const isGreaterGoalCompletionOrder =
    !validateGoalCompletionOrderRange(offerTuneGoals);

  const isBlankPointsAwarded = validatePointsAwarded(offerTuneGoals);

  const isDuplicateTuneGoalID = validateDuplicateGoalIDs(offerTuneGoals);
  const isValidGoalCompletionInstructions =
    validateGoalCompletionInstructions(offerTuneGoals);

  const isBelowMinGoals = validateMinGoals(offerTuneGoals);

  const validZipsAndStates =
    currentOffer &&
    validateOfferInputs(currentOffer.states, 'states').isValid &&
    validateOfferInputs(currentOffer.zips, 'zips').isValid;

  const validPayout =
    currentOffer &&
    validateOfferInputs(currentOffer.mediarate, 'mediarate').isValid;

  const validConversionCap =
    currentOffer && validateOfferInputs(currentOffer.cap, 'cap').isValid;

  const validKloverPoints =
    currentOffer && validateOfferInputs(currentOffer.points, 'points').isValid;

  const isActive = currentOffer && currentOffer.isactive;
  const hasLabel = currentOffer && currentOffer.label;
  const isRevenueConversionEventSupported = currentOffer?.revenueconversionevent
    ? Object.keys(RevenueConversionEvents).includes(
        currentOffer?.revenueconversionevent
      )
    : true;
  const isKloverPointsActionSupported = currentOffer?.actionforpoints
    ? Object.keys(KloverPointsActions).includes(currentOffer?.actionforpoints)
    : true;

  const isValidStartDate =
    !currentOffer?.startdate ||
    (!isActive &&
      getEpochTime(currentOffer?.startdate, 0, 0, 0, 0) > Date.now()) ||
    (isActive &&
      currentOffer?.startdate &&
      getEpochTime(currentOffer?.startdate, 0, 0, 0, 0) < Date.now())
      ? true
      : false;
  const isValidEndDate =
    !currentOffer?.enddate ||
    (currentOffer?.startdate &&
      getEpochTime(currentOffer?.startdate, 23, 59, 59, 999) <=
        getEpochTime(currentOffer?.enddate, 23, 59, 59, 999)) ||
    (!currentOffer?.startdate &&
      getEpochTime(currentOffer?.enddate, 23, 59, 59, 999) >= Date.now())
      ? true
      : false;

  const isBlankPointsChangeType =
    currentOffer?.ispointschangescheduled && !currentOffer?.pointschangetype;

  const isPromoTypeWithInvalidEndDate =
    currentOffer?.ispointschangescheduled &&
    currentOffer?.pointschangetype === ENUM_POINT_CHANGE_TYPE.PROMO &&
    currentOffer?.pointschangestartdate &&
    currentOffer?.pointschangeenddate &&
    moment(currentOffer?.pointschangeenddate).isSameOrBefore(
      moment(currentOffer?.pointschangestartdate)
    );

  const isPromoTypeCorrect =
    (currentOffer?.ispointschangescheduled &&
      currentOffer?.pointschangetype === ENUM_POINT_CHANGE_TYPE.PROMO &&
      (!currentOffer?.pointschangestartdate ||
        !currentOffer?.pointschangeenddate ||
        !currentOffer?.pointschangestartvalue ||
        !currentOffer?.pointschangeendvalue)) ||
    isPromoTypeWithInvalidEndDate;

  const isOneTimeCorrect =
    currentOffer?.ispointschangescheduled &&
    currentOffer?.pointschangetype === ENUM_POINT_CHANGE_TYPE.ONETIME &&
    (!currentOffer?.pointschangestartdate ||
      !currentOffer?.pointschangestartvalue);

  let isValidPointChangeType = false;
  if (currentOffer?.pointschangetype === ENUM_POINT_CHANGE_TYPE.PROMO) {
    isValidPointChangeType = isPromoTypeCorrect;
  } else if (
    currentOffer?.pointschangetype === ENUM_POINT_CHANGE_TYPE.ONETIME
  ) {
    isValidPointChangeType = isOneTimeCorrect;
  }

  const isValidGoalPointChangeType =
    isTuneGoalsPromoType && isTuneGoalsOneTimeType;

  const isBlankCTAUrl = !currentOffer?.ctaurl;
  let isInvalidCTAUrl = false;
  if (currentOffer?.ctaurl) {
    const urlValidation = isValidUrl(
      currentOffer.ctaurl,
      OFFER_FIELD_MAPPING.clickthrough_url,
      ['offer_id']
    );
    isInvalidCTAUrl = !urlValidation.isValid;
  }

  // Date validation using getDateTimeValidationAndValue
  const startPointsChangeDateValidation = getDateTimeValidationAndValue(
    currentOffer?.pointschangestartdate,
    ['pointschangestartdate'],
    null,
    currentOffer
  );
  // Date validation using getDateTimeValidationAndValue
  const endPointsChangeDateValidation =
    currentOffer?.pointschangetype === ENUM_POINT_CHANGE_TYPE.PROMO
      ? getDateTimeValidationAndValue(
          currentOffer?.pointschangeenddate,
          ['pointschangeenddate'],
          null,
          currentOffer
        )
      : { isError: false, errorMessage: '' };

  const goalsPointChangeHaveDateError = isMultipleConversionsEnabled
    ? currentOffer?.offertunegoals
        ?.filter(
          (goal: any) =>
            goal.status !== 'delete' && goal.ispointschangescheduled
        )
        ?.some((goal: any) => {
          const startValidation = getDateTimeValidationAndValue(
            goal?.pointschangestartdate,
            ['pointschangestartdate'],
            goal,
            currentOffer
          );
          const endValidation =
            goal.pointschangetype === ENUM_POINT_CHANGE_TYPE.PROMO
              ? getDateTimeValidationAndValue(
                  goal?.pointschangeenddate,
                  ['pointschangeenddate'],
                  goal,
                  currentOffer
                )
              : { isError: false, errorMessage: '' };
          return startValidation.isError || endValidation.isError;
        })
    : false;

  const isPointsChangeDateError = isMultipleConversionsEnabled
    ? startPointsChangeDateValidation.isError ||
      endPointsChangeDateValidation.isError ||
      goalsPointChangeHaveDateError
    : false;

  // Common goal validation function
  const getGoalValidationError = () => {
    if (isBlankTuneGoalID) {
      return ValidationErrorMessages.TUNE_GOALS_ID_REQUIRED;
    }
    if (isBlankGoalCompletionOrder) {
      return ValidationErrorMessages.TUNE_GOALS_COMPLETION_ORDER_REQUIRED;
    }
    if (isDuplicateTuneGoalOrdering.isDuplicated) {
      return `Duplicate Goal Completion Order : ${isDuplicateTuneGoalOrdering.goalcompletionorder} is not allowed`;
    }
    if (!isTuneGoalsCompletionOrderNumeric) {
      return ValidationErrorMessages.TUNE_GOALS_COMPLETION_ORDER_IS_NOT_A_NUMBER;
    }
    if (isGreaterGoalCompletionOrder) {
      return ValidationErrorMessages.TUNE_GOALS_COMPLETION_ORDER_IS_GREATER;
    }
    if (isDuplicateTuneGoalID.isDuplicated) {
      return `Duplicate Goal ID : ${isDuplicateTuneGoalID.goalid} is not allowed`;
    }
    if (!isValidGoalCompletionInstructions) {
      return ValidationErrorMessages.GOAL_COMPLETION_INSTRUCTION_ERROR;
    }
    if (isBlankPointsAwarded) {
      return ValidationErrorMessages.POINTS_AWARDED_REQUIRED;
    }

    if (isBelowMinGoals) {
      return `At least ${GOAL_LIMITS.MIN_GOALS} goal is required`;
    }
    return null;
  };

  const isDisabled =
    !hasLabel ||
    !validPayout ||
    !validConversionCap ||
    !validKloverPoints ||
    !isValidTuneGoalsPointsAwarded ||
    !isValidTuneGoalsId ||
    !isValidTuneGoalsConversionValue ||
    !isValidStartDate ||
    !isValidEndDate ||
    (isActive && isOfferDisabled(currentOffer)) ||
    !validZipsAndStates ||
    isBelowMinGoals ||
    isBlankTuneGoalID ||
    isBlankGoalCompletionOrder ||
    isDuplicateTuneGoalOrdering.isDuplicated ||
    !isTuneGoalsCompletionOrderNumeric ||
    isGreaterGoalCompletionOrder ||
    isBlankPointsAwarded ||
    isDuplicateTuneGoalID.isDuplicated ||
    !isValidGoalCompletionInstructions ||
    isBlankCTAUrl ||
    isInvalidCTAUrl ||
    isBlankPointsChangeType ||
    isValidPointChangeType ||
    !isValidGoalPointChangeType ||
    isPointsChangeDateError ||
    !isRevenueConversionEventSupported ||
    !isKloverPointsActionSupported;

  const goalValidationError = getGoalValidationError();

  const SaveTitle = !hasLabel
    ? 'Needs a label'
    : isInvalidCTAUrl
    ? ValidationErrorMessages.INVALID_CTA_URL
    : !validZipsAndStates
    ? 'Invalid States or Zip Codes'
    : !validPayout
    ? ValidationErrorMessages.PAYOUT
    : !validConversionCap
    ? ValidationErrorMessages.CONVERSION_CAPPING
    : !validKloverPoints
    ? ValidationErrorMessages.POINTS
    : !isValidTuneGoalsId
    ? ValidationErrorMessages.TUNE_GOALS_ID
    : goalValidationError
    ? goalValidationError
    : !isValidTuneGoalsPointsAwarded
    ? ValidationErrorMessages.TUNE_GOALS_POINTS_AWARDED
    : !isValidTuneGoalsConversionValue
    ? ValidationErrorMessages.TUNE_GOALS_CONVERSION_VALUE
    : !isRevenueConversionEventSupported
    ? ValidationErrorMessages.SUCCESS_URL_NO_LONGER_SUPPORTED
    : !isKloverPointsActionSupported
    ? ValidationErrorMessages.SUCCESS_URL_NO_LONGER_SUPPORTED
    : isPromoTypeWithInvalidEndDate ||
      startPointsChangeDateValidation.isError ||
      endPointsChangeDateValidation.isError
    ? ValidationErrorMessages.POINT_CHANGE_REQUIRED_VALID_DETAILS
    : !isValidStartDate
    ? !isActive &&
      getEpochTime(currentOffer?.startdate, 0, 0, 0, 0) < Date.now()
      ? ValidationErrorMessages.START_DATE_SHOULD_BE_IN_FUTURE
      : ValidationErrorMessages.FUTURE_START_DATE_ACTIVE_OFFER
    : !isValidEndDate
    ? ValidationErrorMessages.END_DATE_EARLIER_THAN_START_DATE
    : !isTuneGoalsPromoType ||
      !isTuneGoalsOneTimeType ||
      goalsPointChangeHaveDateError
    ? ValidationErrorMessages.GOAL_POINT_CHANGE_REQUIRED_VALID_DETAILS
    : isDisabled || isActive
    ? null
    : '';

  const defaultValidation = () => {
    if (!hasLabel) {
      return ValidationErrorMessages.NEEDS_A_LABEL;
    }

    if (isBlankCTAUrl) {
      return ValidationErrorMessages.CTA_URL_REQUIRED;
    }
    if (isInvalidCTAUrl) {
      return ValidationErrorMessages.INVALID_CTA_URL;
    }

    if (goalValidationError) {
      return goalValidationError;
    }

    if (!isRevenueConversionEventSupported) {
      return ValidationErrorMessages.SUCCESS_URL_NO_LONGER_SUPPORTED;
    }

    if (!isKloverPointsActionSupported) {
      return ValidationErrorMessages.SUCCESS_URL_NO_LONGER_SUPPORTED;
    }

    if (isBlankPointsChangeType) {
      return ValidationErrorMessages.POINT_CHANGE_TYPE_REQUIREDS;
    }

    if (
      (currentOffer?.pointschangetype === ENUM_POINT_CHANGE_TYPE.PROMO &&
        isPromoTypeCorrect) ||
      (currentOffer?.pointschangetype === ENUM_POINT_CHANGE_TYPE.ONETIME &&
        isOneTimeCorrect)
    ) {
      return ValidationErrorMessages.POINT_CHANGE_REQUIRED_VALID_DETAILS;
    }

    if (
      currentOffer?.multipleconversionsenabled &&
      (isPromoTypeWithInvalidEndDate ||
        startPointsChangeDateValidation.isError ||
        endPointsChangeDateValidation.isError)
    ) {
      return ValidationErrorMessages.POINT_CHANGE_REQUIRED_VALID_DETAILS;
    }

    if (
      !isTuneGoalsPromoType ||
      !isTuneGoalsOneTimeType ||
      goalsPointChangeHaveDateError
    ) {
      return ValidationErrorMessages.GOAL_POINT_CHANGE_REQUIRED_VALID_DETAILS;
    }

    return '';
  };

  const defaultError = defaultValidation();
  return {
    SaveTitle: currentOffer?.isactive ? SaveTitle : defaultError,
    isDisabled: currentOffer?.isactive ? isDisabled : defaultError !== '',
    isValidStartDate,
    isValidEndDate,
  };
};

export default setSaveOfferButton;
